import Subscription, { ISubscription, ISubscriptionListener } from '@aytm/lib/common/subscription'
import { ModuleID } from '../types'
import MediaSourceSelect from './components/MediaSourceSelect'
import { IWebcamModule } from './types'
import Video from './components/Video'

const mediaStreamConstraintsDefault = { video: true, audio: true }

const videoDevices: MediaDeviceInfo[] = []
const audioDevices: MediaDeviceInfo[] = []

let videoDevice: MediaDeviceInfo | null = null
let audioDevice: MediaDeviceInfo | null = null

let moduleLoaded = false
let moduleReady = false
let blind = false
let videoUrl: string | null = null
let blobs: Blob | null = null
let stream: MediaStream | null = null
let recorder: MediaRecorder | null = null
let videoElement: HTMLVideoElement | null = null
let needAudio = false
let needVideo = true

let updateSubscription: ISubscription<IWebcamModule> = null

const delay = (timeout = 100) => new Promise<void>(resolve => setTimeout(() => resolve(), timeout))

const getMimeType = () => {
  if (!needVideo) {
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return { mimeType: 'audio/webm;codecs=opus' }
    return { mimeType: 'audio/mp4' }
  }
  if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
    return { mimeType: 'video/webm; codecs=vp9' }
  }
  if (MediaRecorder.isTypeSupported('video/webm; codecs=vp8')) {
    return { mimeType: 'video/webm; codecs=vp8,opus' }
  }
  return { mimeType: 'video/mp4' }
}

const stopCamera = () => {
  if (!stream) return
  const tracks = stream.getTracks()
  for (const track of tracks) {
    track.stop()
  }
}

const startCamera = async (
  constraints: MediaStreamConstraints = mediaStreamConstraintsDefault,
  retries = 10
): Promise<void> => {
  if (!moduleReady) throw new Error('WebcamModule not available')
  try {
    stream = await navigator.mediaDevices.getUserMedia(constraints)
    return Promise.resolve()
  } catch (error) {
    if (retries > 0) {
      await delay(1000)
      return startCamera(constraints, retries - 1)
    }
    return Promise.reject(error)
  }
}

const WebcamModule: IWebcamModule = {
  get id(): ModuleID {
    return ModuleID.WEBCAM
  },
  config() {
    return WebcamModule
  },
  // eslint-disable-next-line @typescript-eslint/require-await
  async load() {
    if (!WebcamModule.loaded) {
      moduleLoaded = true
    }
    return Promise.resolve(WebcamModule)
  },
  async init(audio: true, video: true) {
    if (moduleReady || blind) return true
    needAudio = audio
    needVideo = video
    try {
      await navigator.mediaDevices
        .getUserMedia({ video: video, audio: audio })
        .then(newStream => {
          stream = newStream
          for (const track of stream.getTracks()) {
            track.stop()
          }
          const devices = navigator.mediaDevices.enumerateDevices()
          return devices
        })
        .then(devices => {
          for (const device of devices) {
            if ('videoinput' === device.kind) {
              videoDevices.push(device)
            }
            if ('audioinput' === device.kind) {
              audioDevices.push(device)
            }
          }
          videoDevice = videoDevices[0] || null
          audioDevice = audioDevices[0] || null
          moduleReady = true
          if (null != updateSubscription) {
            updateSubscription.update(WebcamModule)
          }
          startCamera({ audio: needAudio, video: needVideo })
            .then(() => {
              if (needVideo && videoElement) videoElement.srcObject = stream

              if (null != updateSubscription) {
                updateSubscription.update(WebcamModule)
              }
            })
            // eslint-disable-next-line no-console
            .catch(error => console.log(error))
        })
        .then(() => {
          if (null != updateSubscription) {
            updateSubscription.update(WebcamModule)
          }
        })
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      return false
    }
  },
  get loaded() {
    return moduleLoaded
  },
  get ready() {
    return moduleReady
  },
  get recorder() {
    return recorder
  },
  get available() {
    return Boolean(navigator.mediaDevices && 'function' === typeof navigator.mediaDevices.getUserMedia)
  },
  get audioDevice() {
    return audioDevice
  },
  get videoDevice() {
    return videoDevice
  },
  get stream() {
    return stream
  },
  set audioDevice(device: MediaDeviceInfo) {
    audioDevice = device
    void this.switchCamera()
  },
  set videoDevice(device: MediaDeviceInfo) {
    videoDevice = device
    void this.switchCamera()
  },
  get SourceSelect() {
    return MediaSourceSelect
  },
  get Video() {
    return Video
  },
  set video(video: HTMLVideoElement) {
    videoElement = video
    if (stream && stream.active) {
      videoElement.srcObject = stream
    }
  },
  set blind(b: boolean) {
    blind = b
    // if (null != updateSubscription) {
    //   updateSubscription.update(WebcamModule)
    // }
  },
  get videoUrl(): string | null {
    return videoUrl
  },
  get blobs(): Blob | null {
    return blobs
  },
  get shot() {
    if (!stream || !stream.active) return null
    const canvas = document.createElement('canvas')
    canvas.width = videoElement.clientWidth + 40
    canvas.height = videoElement.clientHeight + 30
    canvas.getContext('2d').drawImage(videoElement, 0, 0)
    return canvas.toDataURL('image/webp')
  },
  sourceList(type: 'video' | 'audio') {
    return 'video' === type ? videoDevices : audioDevices
  },
  async switchCamera() {
    try {
      stopCamera()
      await startCamera({
        video: { deviceId: { exact: videoDevice.deviceId } },
        audio: { deviceId: { exact: audioDevice.deviceId } }
      }).then(() => {
        videoElement.srcObject = stream
        if (null != updateSubscription) {
          updateSubscription.update(WebcamModule)
        }
      })
      return true
    } catch {
      return false
    }
  },
  startRecording() {
    if (!stream || !stream.active) return
    blobs = null
    const options = getMimeType()
    recorder = new MediaRecorder(stream, options)
    recorder.ondataavailable = event => {
      blobs = new Blob([event.data], { type: options.mimeType })
      videoUrl = URL.createObjectURL(blobs)
      if (null != updateSubscription) {
        updateSubscription.update(WebcamModule)
      }
    }
    recorder.start()
  },
  stopRecording() {
    if (recorder) recorder.stop()
  },
  stopStreams() {
    stopCamera()
    moduleReady = false
    if (null != updateSubscription) {
      updateSubscription.update(WebcamModule)
    }
  },
  onUpdate(listener: ISubscriptionListener<IWebcamModule>) {
    if (null == updateSubscription) {
      updateSubscription = Subscription<IWebcamModule>()
    }
    return updateSubscription.listen(listener)
  }
}

export default WebcamModule
